import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useSession } from '../state/session.tsx';

/**
 * Whether this session is a visitor in the shared public demo.
 *
 * The one question every demo-limited screen asks, so they all ask it the same
 * way: `publicDemo` is null for every ordinary session.
 */
export function useIsPublicDemo(): boolean {
  return Boolean(useSession().publicDemo);
}

/**
 * The note on a screen the shared public demo deliberately limits.
 *
 * The server refuses these paths in the demo (shared/public-demo.ts), and a
 * screen that simply fails, or renders nothing, reads as broken software. This
 * says instead what the demo is not doing and why, and what the screen does in
 * an account of one's own, in the brand colours of the demo banner so the two
 * read as the same voice.
 *
 * Renders nothing outside the demo, so a caller can place it unconditionally.
 */
export default function DemoLimitNote({ children, className = '' }: { children: ReactNode; className?: string }) {
  const { t } = useTranslation();
  if (!useIsPublicDemo()) return null;
  return (
    <div
      role="note"
      className={`rounded-lg border border-[color:var(--color-brand)] bg-[color:var(--color-brand-soft)] px-3 py-2 text-sm text-[color:var(--color-brand-strong)] ${className}`}
    >
      <span className="mr-2 font-bold tracking-wide uppercase">{t('publicDemo.limitBadge')}</span>
      {children}
    </div>
  );
}

/**
 * A whole settings section the shared demo refuses, in place of the section.
 *
 * Those sections load from an endpoint the demo blocks outright, and each one
 * rendered nothing at all when the load failed: the settings rail named
 * Billing, Referrals and the rest, and clicking them scrolled to a blank. The
 * heading stays, so the rail still lands somewhere, with the reason under it.
 */
export function DemoLimitedSection({ heading, note }: { heading: string; note: string }) {
  return (
    <section className="card mb-6">
      <h2 className="mb-3 text-lg font-semibold">{heading}</h2>
      <DemoLimitNote>{note}</DemoLimitNote>
    </section>
  );
}
